import type { DailyItem } from './daily-items';
import type { AdminTaskTemplateItem } from './admin-task-items';

/**
 * Valor por defecto cuando aún no se ha cargado la configuración desde la BD.
 * El nombre real del área se gestiona en `app_settings.admin_tasks_area_name`
 * y se obtiene server-side con `getAdminTasksAreaName()` o client-side via el
 * hook `useAreaName()` del AppSettings context.
 */
export const ADMIN_TASKS_AREA_NAME_DEFAULT = 'Administración';

export const FREQUENCIES = [
  { key: 'once',      label: 'Única' },
  { key: 'daily',     label: 'Diaria' },
  { key: 'weekly',    label: 'Semanal' },
  { key: 'biweekly',  label: 'Quincenal' },
  { key: 'monthly',   label: 'Mensual' },
  { key: 'quarterly', label: 'Trimestral' },
  { key: 'yearly',    label: 'Anual' },
] as const;

export type AdminTaskFrequency = typeof FREQUENCIES[number]['key'];

export const ADMIN_TASK_FREQUENCY_KEYS: AdminTaskFrequency[] =
  FREQUENCIES.map(f => f.key);

export function frequencyLabel(key: string): string {
  return FREQUENCIES.find(f => f.key === key)?.label || key;
}

export const TASK_STATUSES = [
  { key: 'pending',     label: 'Pendiente' },
  { key: 'in_progress', label: 'En progreso' },
  { key: 'done',        label: 'Completada' },
  { key: 'cancelled',   label: 'Cancelada' },
] as const;

export type AdminTaskStatus = typeof TASK_STATUSES[number]['key'];

export const ADMIN_TASK_STATUS_KEYS: AdminTaskStatus[] =
  TASK_STATUSES.map(s => s.key);

export function statusLabel(key: string): string {
  return TASK_STATUSES.find(s => s.key === key)?.label || key;
}

export interface AdminTask {
  id: number;
  title: string;
  description: string;
  assignee_id: number;
  assignee_email?: string;
  assignee_name?: string;
  created_by: number | null;
  creator_name?: string | null;
  frequency: AdminTaskFrequency;
  due_date?: string | null;
  due_time?: string | null;
  recurrence_weekdays?: string | null;
  recurrence_day?: string | null;
  recurrence_month?: number | null;
  starts_on?: string | null;
  schedule_from?: string | null;
  status: AdminTaskStatus;
  created_at: string;
  updated_at: string;
  /** Checklist que define el administrador; el usuario asignado no lo edita. */
  items?: AdminTaskTemplateItem[];
}

/** Día del mes que significa "el último, sea cual sea la longitud del mes". */
export const LAST_DAY = 'last';

export const MONTHS: { value: number; label: string }[] = [
  { value: 1, label: 'Enero' },      { value: 2, label: 'Febrero' },
  { value: 3, label: 'Marzo' },      { value: 4, label: 'Abril' },
  { value: 5, label: 'Mayo' },       { value: 6, label: 'Junio' },
  { value: 7, label: 'Julio' },      { value: 8, label: 'Agosto' },
  { value: 9, label: 'Septiembre' }, { value: 10, label: 'Octubre' },
  { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' },
];

/** Frecuencias que se programan con un día del mes (y, algunas, con un mes). */
export function frequencyUsesMonthDay(freq: string): boolean {
  return freq === 'monthly' || freq === 'quarterly' || freq === 'yearly';
}

/** Frecuencias que además necesitan saber en qué mes cae (o arranca) el ciclo. */
export function frequencyUsesMonth(freq: string): boolean {
  return freq === 'quarterly' || freq === 'yearly';
}

export const WEEKDAYS: { value: number; short: string; long: string }[] = [
  { value: 1, short: 'L',  long: 'Lunes' },
  { value: 2, short: 'M',  long: 'Martes' },
  { value: 3, short: 'X',  long: 'Miércoles' },
  { value: 4, short: 'J',  long: 'Jueves' },
  { value: 5, short: 'V',  long: 'Viernes' },
  { value: 6, short: 'S',  long: 'Sábado' },
  { value: 0, short: 'D',  long: 'Domingo' },
];

export function parseWeekdays(raw: string | null | undefined): number[] {
  if (!raw) return [];
  return raw.split(',')
    .map(s => Number(s.trim()))
    .filter(n => Number.isInteger(n) && n >= 0 && n <= 6);
}

export function serializeWeekdays(days: number[]): string | null {
  const clean = Array.from(new Set(days.filter(n => Number.isInteger(n) && n >= 0 && n <= 6))).sort();
  return clean.length === 0 ? null : clean.join(',');
}

export function weekdaysLabel(raw: string | null | undefined): string {
  const days = parseWeekdays(raw);
  if (days.length === 0) return '';
  return WEEKDAYS
    .filter(w => days.includes(w.value))
    .map(w => w.short)
    .join(' ');
}

/**
 * Frequencies that show the weekday selector (Mon..Sun) in the form
 * and use it to determine daily occurrences.
 */
export function frequencyUsesWeekdays(freq: string): boolean {
  return freq === 'weekly' || freq === 'biweekly';
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function parseYmd(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

function diffDays(a: Date, b: Date): number {
  const ms = a.getTime() - b.getTime();
  return Math.floor(ms / (24 * 60 * 60 * 1000));
}

/**
 * Regla **anterior** de recurrencia, conservada tal cual.
 *
 * No es código muerto: el reporte de cumplimiento recalcula las ocurrencias de
 * cualquier semana a partir de las reglas, así que cambiarlas movería el pasado.
 * Las tareas que existían cuando se soltó la programación explícita llevan
 * `schedule_from` con esa fecha, y todo lo anterior se sigue evaluando aquí.
 *
 * Sus defectos son la razón del cambio, y quedan documentados por si alguien
 * lee una cifra vieja: el ancla es invisible (la fecha de creación si no hay
 * `due_date`), el día 31 se salta los meses cortos —cinco al año—, un 29 de
 * febrero anual solo cae en año bisiesto y una semanal sin días no ocurre nunca.
 */
export function isLegacyOccurrenceOn(task: {
  frequency: string;
  due_date?: string | null;
  recurrence_weekdays?: string | null;
  created_at?: string | Date | null;
}, date: Date): boolean {
  const today = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const baseDate = task.due_date
    ? parseYmd(String(task.due_date).slice(0, 10))
    : task.created_at
      ? new Date(new Date(task.created_at).getFullYear(), new Date(task.created_at).getMonth(), new Date(task.created_at).getDate())
      : today;

  switch (task.frequency) {
    case 'once':
      return !!task.due_date && ymd(today) === ymd(baseDate);
    case 'daily':
      return true;
    case 'weekly': {
      const days = parseWeekdays(task.recurrence_weekdays);
      if (days.length === 0) return false;
      return days.includes(today.getDay());
    }
    case 'biweekly': {
      const days = parseWeekdays(task.recurrence_weekdays);
      if (days.length === 0) return false;
      if (!days.includes(today.getDay())) return false;
      const weeks = Math.floor(diffDays(today, baseDate) / 7);
      return weeks >= 0 && weeks % 2 === 0;
    }
    case 'monthly':
      return today.getDate() === baseDate.getDate();
    case 'quarterly':
      return today.getDate() === baseDate.getDate()
        && (today.getMonth() - baseDate.getMonth()) % 3 === 0;
    case 'yearly':
      return today.getDate() === baseDate.getDate() && today.getMonth() === baseDate.getMonth();
    default:
      return false;
  }
}

export function formatYmdLocal(d: Date): string {
  return ymd(d);
}

export interface OccurrenceSpec {
  frequency: string;
  due_date?: string | null;
  recurrence_weekdays?: string | null;
  /** Día del mes: '1'…'31' o `LAST_DAY`. */
  recurrence_day?: string | null;
  /** Mes (1–12): el del ciclo anual, o el de arranque del trimestral. */
  recurrence_month?: number | null;
  /** No hay ocurrencias antes de esta fecha, aunque la regla las produzca. */
  starts_on?: string | null;
  /**
   * Fecha desde la que manda la programación explícita. Antes de ella se evalúa
   * con `isLegacyOccurrenceOn`, para no mover cifras ya reportadas. `null` = la
   * tarea nació con las reglas nuevas y siempre se evalúa con ellas.
   */
  schedule_from?: string | null;
  created_at?: string | Date | null;
}

function daysInMonth(year: number, month0: number): number {
  return new Date(year, month0 + 1, 0).getDate();
}

/**
 * Día del mes que le toca a un mes concreto.
 *
 * Un día 31 en un mes de 30 **cae en el 30**, no se salta el mes. Es lo que
 * hacen cron (`L`), las agendas y lo que espera quien programó "fin de mes";
 * saltárselo dejaba cinco meses del año sin la tarea y sin decirlo.
 */
export function resolveMonthDay(year: number, month0: number, day: string | null | undefined, fallback: number): number {
  const ultimo = daysInMonth(year, month0);
  if (day === LAST_DAY) return ultimo;
  const n = Number(day);
  const elegido = Number.isInteger(n) && n >= 1 && n <= 31 ? n : fallback;
  return Math.min(elegido, ultimo);
}

/** Fecha de la que se leen los valores que la tarea no tiene explícitos. */
function baseDateOf(task: OccurrenceSpec, today: Date): Date {
  const raw = task.starts_on || task.due_date;
  if (raw) return parseYmd(String(raw).slice(0, 10));
  if (task.created_at) {
    const c = new Date(task.created_at);
    return new Date(c.getFullYear(), c.getMonth(), c.getDate());
  }
  return today;
}

/** Regla vigente: la programación es explícita y visible en el formulario. */
export function isScheduledOn(task: OccurrenceSpec, date: Date): boolean {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  if (task.starts_on && ymd(day) < String(task.starts_on).slice(0, 10)) return false;

  const base = baseDateOf(task, day);
  const mesAncla = Number.isInteger(task.recurrence_month) && (task.recurrence_month as number) >= 1
    && (task.recurrence_month as number) <= 12
    ? (task.recurrence_month as number) - 1
    : base.getMonth();
  const diaDelMes = () => resolveMonthDay(day.getFullYear(), day.getMonth(), task.recurrence_day, base.getDate());

  switch (task.frequency) {
    case 'once':
      return !!task.due_date && ymd(day) === ymd(parseYmd(String(task.due_date).slice(0, 10)));
    case 'daily':
      return true;
    case 'weekly': {
      const days = parseWeekdays(task.recurrence_weekdays);
      return days.length > 0 && days.includes(day.getDay());
    }
    case 'biweekly': {
      const days = parseWeekdays(task.recurrence_weekdays);
      if (days.length === 0 || !days.includes(day.getDay())) return false;
      const semanas = Math.floor(diffDays(day, base) / 7);
      return semanas >= 0 && semanas % 2 === 0;
    }
    case 'monthly':
      return day.getDate() === diaDelMes();
    case 'quarterly': {
      // El resto de un negativo es negativo en JS: se normaliza para que un mes
      // anterior al de arranque caiga en el trimestre correcto.
      const salto = (((day.getMonth() - mesAncla) % 3) + 3) % 3;
      return salto === 0 && day.getDate() === diaDelMes();
    }
    case 'yearly':
      return day.getMonth() === mesAncla && day.getDate() === diaDelMes();
    default:
      return false;
  }
}

/**
 * Si la tarea ocurre en `date`. Antes de `schedule_from` responde la regla
 * anterior: corregir la programación no puede reescribir un cumplimiento que ya
 * se reportó con las reglas de entonces.
 */
/**
 * Columnas que hay que traer para poder decidir si una tarea ocurre. Se usa en
 * los `SELECT` porque olvidar una no rompe nada visible: la tarea simplemente
 * ocurre otro día, y eso se descubre semanas después.
 */
export const OCCURRENCE_COLUMNS =
  'frequency, due_date, recurrence_weekdays, recurrence_day, recurrence_month, starts_on, schedule_from, created_at';

/** El mismo juego de campos, ya en forma de spec. */
export function occurrenceSpecOf(row: {
  frequency: string;
  due_date?: string | Date | null;
  recurrence_weekdays?: string | null;
  recurrence_day?: string | null;
  recurrence_month?: number | null;
  starts_on?: string | Date | null;
  schedule_from?: string | Date | null;
  created_at?: string | Date | null;
}): OccurrenceSpec {
  const fecha = (v: string | Date | null | undefined) =>
    v instanceof Date ? formatYmdLocal(v) : v ? String(v).slice(0, 10) : null;
  return {
    frequency: row.frequency,
    due_date: fecha(row.due_date),
    recurrence_weekdays: row.recurrence_weekdays ?? null,
    recurrence_day: row.recurrence_day ?? null,
    recurrence_month: row.recurrence_month ?? null,
    starts_on: fecha(row.starts_on),
    schedule_from: fecha(row.schedule_from),
    created_at: row.created_at ?? null,
  };
}

export function isOccurrenceOn(task: OccurrenceSpec, date: Date): boolean {
  if (task.schedule_from && ymd(date) < String(task.schedule_from).slice(0, 10)) {
    return isLegacyOccurrenceOn(task, date);
  }
  return isScheduledOn(task, date);
}

/**
 * Ventana máxima que se recorre buscando la siguiente ocurrencia. Un año y
 * pico cubre la frecuencia más larga (anual) sin volver el cálculo abierto.
 */
const OCCURRENCE_HORIZON_DAYS = 400;

/**
 * Fecha de la próxima ocurrencia de la tarea desde `from` (inclusive), o `null`
 * si ya no vuelve a ocurrir (p. ej. una tarea única cuyo día pasó). Se recorre
 * día por día porque `isOccurrenceOn` es la única fuente de verdad de las
 * reglas de recurrencia.
 */
export function nextOccurrenceFrom(task: OccurrenceSpec, from: Date): Date | null {
  // Arrancar en el inicio de la programación evita gastar el horizonte
  // recorriendo días anteriores a que la tarea siquiera exista.
  const inicio = task.starts_on ? parseYmd(String(task.starts_on).slice(0, 10)) : null;
  const desde = inicio && inicio.getTime() > from.getTime() ? inicio : from;
  const start = new Date(desde.getFullYear(), desde.getMonth(), desde.getDate());
  for (let i = 0; i < OCCURRENCE_HORIZON_DAYS; i++) {
    const d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    if (isOccurrenceOn(task, d)) return d;
  }
  return null;
}

/** Los próximos `cuantos` cierres. Es lo que el formulario muestra al configurar. */
export function nextOccurrences(task: OccurrenceSpec, from: Date, cuantos: number): Date[] {
  const salida: Date[] = [];
  let cursor = from;
  for (let i = 0; i < cuantos; i++) {
    const siguiente = nextOccurrenceFrom(task, cursor);
    if (!siguiente) break;
    salida.push(siguiente);
    cursor = new Date(siguiente.getFullYear(), siguiente.getMonth(), siguiente.getDate() + 1);
  }
  return salida;
}

function dayLabel(day: string | null | undefined, fallback?: number): string {
  if (day === LAST_DAY) return 'el último día';
  const n = Number(day);
  if (Number.isInteger(n) && n >= 1 && n <= 31) return `el día ${n}`;
  return fallback ? `el día ${fallback}` : 'sin día definido';
}

/** Cómo se lee la programación de una tarea, en una línea. */
export function scheduleSummary(task: OccurrenceSpec): string {
  switch (task.frequency) {
    case 'once':
      return task.due_date
        ? parseYmd(String(task.due_date).slice(0, 10)).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })
        : 'sin fecha';
    case 'daily':
      return 'todos los días';
    case 'weekly':
    case 'biweekly': {
      const dias = WEEKDAYS.filter(w => parseWeekdays(task.recurrence_weekdays).includes(w.value)).map(w => w.long.toLowerCase());
      if (dias.length === 0) return 'sin días definidos';
      const cada = task.frequency === 'weekly' ? 'cada' : 'cada dos semanas, en';
      return `${cada} ${dias.join(', ')}`;
    }
    case 'monthly':
      return `${dayLabel(task.recurrence_day)} de cada mes`;
    case 'quarterly': {
      const inicio = (task.recurrence_month ?? 1) - 1;
      const meses = [0, 3, 6, 9].map(k => MONTHS[(((inicio + k) % 12) + 12) % 12].label.toLowerCase());
      return `${dayLabel(task.recurrence_day)} de ${meses.join(', ')}`;
    }
    case 'yearly': {
      const mes = MONTHS.find(m => m.value === task.recurrence_month)?.label.toLowerCase();
      return `${dayLabel(task.recurrence_day)} de ${mes ?? 'cada año'}`;
    }
    default:
      return '';
  }
}

/**
 * Comprueba que la programación esté completa. Devuelve el motivo o `null`.
 *
 * Una tarea mal programada no falla: simplemente no ocurre nunca, en silencio.
 * Por eso se rechaza al guardar y no al ejecutarla.
 */
export function validateSchedule(task: OccurrenceSpec): string | null {
  if (task.frequency === 'once' && !task.due_date) {
    return 'Una tarea única necesita su fecha';
  }
  if (frequencyUsesWeekdays(task.frequency) && parseWeekdays(task.recurrence_weekdays).length === 0) {
    return 'Debes elegir al menos un día de la semana';
  }
  if (frequencyUsesMonthDay(task.frequency)) {
    const d = task.recurrence_day;
    const n = Number(d);
    if (d !== LAST_DAY && !(Number.isInteger(n) && n >= 1 && n <= 31)) {
      return 'Debes elegir el día del mes (1 al 31 o el último día)';
    }
  }
  if (frequencyUsesMonth(task.frequency)) {
    const m = task.recurrence_month;
    if (!Number.isInteger(m) || (m as number) < 1 || (m as number) > 12) {
      return task.frequency === 'yearly'
        ? 'Debes elegir el mes'
        : 'Debes elegir el mes en que arranca el trimestre';
    }
  }
  return null;
}

/**
 * Ocurrencia "abierta" de la tarea: la de hoy si hoy toca, si no la siguiente.
 * Es la fecha contra la que se guarda el avance, para que trabajar una tarea
 * mensual el día 10 acumule en el cierre del día 15 y no en un día suelto.
 * Sin ocurrencias futuras (tarea única vencida) el avance se registra hoy.
 */
export function currentOccurrenceDate(task: OccurrenceSpec, today: Date = new Date()): Date {
  return nextOccurrenceFrom(task, today)
    ?? new Date(today.getFullYear(), today.getMonth(), today.getDate());
}

/** Etiqueta larga del próximo cierre, con su hora límite cuando la tiene. */
export function occurrenceLabel(date: Date, dueTime?: string | null): string {
  const fecha = date.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' });
  return dueTime ? `${fecha} · ⏰ ${dueTime}` : fecha;
}

/** Días entre hoy y la fecha dada (negativo si ya pasó). */
export function daysUntil(date: Date, from: Date = new Date()): number {
  const a = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const b = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  return Math.round((a.getTime() - b.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * Alta/baja idempotente de una tarea administrativa dentro de un punto del daily
 * (el campo "¿Qué completé?"). Se modela como un registro de texto libre marcado
 * con `adminTaskId` para poder quitarlo al desmarcar el check. No lleva horas, por
 * lo que no bloquea el guardado.
 */
export function appendAdminTaskItem(items: DailyItem[], taskId: number, title: string): DailyItem[] {
  const without = items.filter(it => it.adminTaskId !== taskId);
  return [...without, { kind: 'custom', label: title, hours: null, adminTaskId: taskId }];
}

export function removeAdminTaskItem(items: DailyItem[], taskId: number): DailyItem[] {
  return items.filter(it => it.adminTaskId !== taskId);
}
